import { Button, Flex, useDisclosure } from '@chakra-ui/react';
import React, { useEffect } from 'react';
import {
  FormProvider,
  UseFieldArrayReturn,
  useFieldArray,
  useForm,
} from 'react-hook-form';

import { getFormValues } from './dataMappings/getFormValues';
import { setFormValues } from './dataMappings/setFormValues';
import { FormFields } from './types';
import { Icon } from 'packages/common-frontend/components/icons';
import { Typography } from 'packages/common-frontend/components/Typography';
import { useScrollToAnchor } from 'packages/common-frontend/hooks/useScrollToAnchor';
import { AddPartyModal } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/parties/AddPartyModal';
import { BankAccounts } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/parties/BankAccounts';
import {
  usePartiesDiffValues,
  usePartyDiffResult,
} from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/parties/hooks';
import { AutoSave } from '~/core/routes/settings/implementations/common/AutoSave';
import { useConfirmDeleteContext } from '~/core/routes/settings/implementations/common/ConfirmDeleteModalContext';
import {
  useCreditAgreementStore,
  useGetCreditAgreementLazy,
} from '~/core/routes/settings/implementations/common/CreditAgreementProvider/CreditAgreementProvider';
import {
  InputCardWithDiff,
  ShowWhileEditing,
} from '~/core/routes/settings/implementations/common/DiffInputs';
import { TextInput } from '~/core/routes/settings/implementations/common/Inputs';
import { Party } from '~/core/routes/settings/implementations/common/types';

type FormComponentProps = {
  partyFieldArray: UseFieldArrayReturn<
    FormFields,
    | 'wipForm.borrowers'
    | 'wipForm.capitalProviders'
    | 'diffForm.borrowers'
    | 'diffForm.capitalProviders'
  >;
  partyIndex: number;
  partyId: string;
};

const BorrowerPartyInputs = ({
  partyFieldArray,
  partyIndex,
  partyId,
}: FormComponentProps) => {
  const { showConfirmDeleteModal } = useConfirmDeleteContext();

  const party = partyFieldArray.fields[partyIndex];

  const diff = usePartyDiffResult('borrowers', partyId);

  return (
    <InputCardWithDiff
      diff={diff?.diffResult}
      deleteProperty={() =>
        showConfirmDeleteModal(party.name, () =>
          partyFieldArray.remove(partyIndex),
        )
      }
    >
      <Typography variant="section-subtitle">{party.name}</Typography>
      <TextInput<FormFields>
        name={`wipForm.borrowers.${partyIndex}.description`}
        label="Description"
        helperText="Optional"
      />
      <BankAccounts
        partyType="borrowers"
        party={party}
        parentName={`wipForm.borrowers.${partyIndex}`}
      />
    </InputCardWithDiff>
  );
};

const LenderPartyInputs = ({
  partyFieldArray,
  partyIndex,
  partyId,
}: FormComponentProps) => {
  const { showConfirmDeleteModal } = useConfirmDeleteContext();

  const party = partyFieldArray.fields[partyIndex];
  const diff = usePartyDiffResult('capitalProviders', partyId);

  return (
    <InputCardWithDiff
      diff={diff?.diffResult}
      deleteProperty={() =>
        showConfirmDeleteModal(party.name, () =>
          partyFieldArray.remove(partyIndex),
        )
      }
    >
      <Typography variant="section-subtitle">{party.name}</Typography>
      <BankAccounts
        partyType="capitalProviders"
        party={party}
        parentName={`wipForm.capitalProviders.${partyIndex}`}
      />
    </InputCardWithDiff>
  );
};

type PartyFieldWithDiffProps = {
  partyType: 'servicers' | 'verificationAgents';
  partyId: string;
  name: string;
  index: number;
  remove: (index: number) => void;
};

export const PartyFieldWithDiff = ({
  partyType,
  partyId,
  name,
  index,
  remove,
}: PartyFieldWithDiffProps) => {
  const { showConfirmDeleteModal } = useConfirmDeleteContext();
  const diff = usePartyDiffResult(partyType, partyId);

  return (
    <InputCardWithDiff
      diff={diff?.diffResult}
      deleteProperty={() => showConfirmDeleteModal(name, () => remove(index))}
    >
      <Flex direction="column" justify="center" height="100%">
        <Typography variant="section-subtitle" trim>
          {diff?.currentParty?.name ?? diff?.previousParty?.name}
        </Typography>
      </Flex>
    </InputCardWithDiff>
  );
};

/* 
  This array determines how the NavMenu component renders the names of 
  the sections in this page. If you add a new headline and anchor to this page,
  then add an entry to this array as well.
*/
export const PARTIES = [
  { anchor: 'borrowers', label: 'Borrowers' },
  { anchor: 'capitalProviders', label: 'Capital Providers' },
  { anchor: 'servicers', label: 'Servicers' },
  { anchor: 'verificationAgents', label: 'Verification Agents' },
] as const;

const PartiesBase = ({ formKey }: { formKey: 'wipForm' | 'diffForm' }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  // `keyName` defaults to 'id', which causes react-hook-form to overwrite the
  // real `id` field on the party.
  const borrowers = useFieldArray<
    FormFields,
    `${typeof formKey}.borrowers`,
    'unusedThrowawayId'
  >({
    name: `${formKey}.borrowers`,
    keyName: 'unusedThrowawayId',
  });
  const capitalProviders = useFieldArray<
    FormFields,
    `${typeof formKey}.capitalProviders`,
    'unusedThrowawayId'
  >({
    name: `${formKey}.capitalProviders`,
    keyName: 'unusedThrowawayId',
  });
  const verificationAgents = useFieldArray<
    FormFields,
    `${typeof formKey}.verificationAgents`,
    'unusedThrowawayId'
  >({
    name: `${formKey}.verificationAgents`,
    keyName: 'unusedThrowawayId',
  });
  const servicers = useFieldArray<
    FormFields,
    `${typeof formKey}.servicers`,
    'unusedThrowawayId'
  >({
    name: `${formKey}.servicers`,
    keyName: 'unusedThrowawayId',
  });

  const onAddParty = (party: Party) => {
    const newParty = {
      id: party.id,
      slug: party.slug,
      name: party.name,
      type: party.type,
    };

    switch (party.type) {
      case 'borrower':
        borrowers.append({ ...newParty, description: '', bankAccounts: [] });
        break;
      case 'capitalProvider':
        capitalProviders.append({
          ...newParty,
          bankAccounts: [],
        });
        break;
      case 'verificationAgent':
        verificationAgents.append(newParty);
        break;
      case 'servicer':
        servicers.append(newParty);
        break;
      default:
        break;
    }
  };

  return (
    <Flex direction="column">
      <AddPartyModal
        isOpen={isOpen}
        onClose={onClose}
        onAddParty={onAddParty}
      />
      <Flex justify="space-between" mb="1rem">
        <Typography variant="page-title">Parties</Typography>
        <ShowWhileEditing>
          <Button
            colorScheme="v2-aurora"
            leftIcon={<Icon icon="plus" />}
            onClick={onOpen}
            variant="outline"
          >
            Add Party
          </Button>
        </ShowWhileEditing>
      </Flex>
      <Flex direction="column" gap="0.5rem">
        <AutoSave<FormFields> onSave={setFormValues} />
        <Flex direction="column" mb="1rem">
          <Typography
            variant="section-subtitle"
            TextProps={{ id: 'borrowers' }}
          >
            Borrower
          </Typography>
          <Typography variant="base-muted">
            Exactly one borrower is required and permitted.
          </Typography>
        </Flex>

        {borrowers.fields.map((borrowerField, index) => (
          <BorrowerPartyInputs
            key={borrowerField.id}
            partyFieldArray={borrowers}
            partyIndex={index}
            partyId={borrowerField.id}
          />
        ))}

        <Flex direction="column" mb="1rem">
          <Typography
            variant="section-subtitle"
            TextProps={{ id: 'capitalProviders' }}
          >
            Lenders
          </Typography>
          <Typography variant="base-muted">
            At least one lender is required.
          </Typography>
        </Flex>

        {capitalProviders.fields.map((capitalProviderField, index) => (
          <LenderPartyInputs
            key={capitalProviderField.id}
            partyFieldArray={capitalProviders}
            partyIndex={index}
            partyId={capitalProviderField.id}
          />
        ))}

        {servicers.fields.length > 0 && (
          <Flex direction="column">
            <Typography
              variant="section-subtitle"
              TextProps={{ id: 'servicers' }}
            >
              Servicers
            </Typography>
          </Flex>
        )}

        {servicers.fields.map((servicerField, index) => (
          <PartyFieldWithDiff
            name={servicerField.name}
            partyId={servicerField.id}
            index={index}
            key={servicerField.id}
            partyType="servicers"
            remove={() => servicers.remove(index)}
          />
        ))}

        {verificationAgents.fields.length > 0 && (
          <Flex direction="column">
            <Typography
              variant="section-subtitle"
              TextProps={{ id: 'verificationAgents' }}
            >
              Verification Agents
            </Typography>
          </Flex>
        )}

        {verificationAgents.fields.map((verificationAgentField, index) => (
          <PartyFieldWithDiff
            name={verificationAgentField.name}
            partyId={verificationAgentField.id}
            index={index}
            key={verificationAgentField.id}
            partyType="verificationAgents"
            remove={() => verificationAgents.remove(index)}
          />
        ))}
      </Flex>
    </Flex>
  );
};

export const Parties = () => {
  useScrollToAnchor();

  const formKey = useCreditAgreementStore((store) =>
    store.isEditing ? 'wipForm' : 'diffForm',
  );

  const getCreditAgreementOrThrow = useGetCreditAgreementLazy();

  const form = useForm<FormFields>({
    defaultValues: {
      wipForm: getFormValues(getCreditAgreementOrThrow()),
      diffForm: undefined,
    },
  });

  const diffValues = usePartiesDiffValues();

  useEffect(() => {
    form.setValue('diffForm', diffValues);
  }, [form, diffValues]);

  return (
    <FormProvider {...form}>
      <PartiesBase key={`parties-${formKey}`} formKey={formKey} />
    </FormProvider>
  );
};
